import { useCallback } from 'react'
import { isVisitTypeSelectableForHotel } from '@components/VisitTypeCategoriesAccordion/helpers'
import { resolveNextAccordion } from '@utils/accordion'
import { getKeyLang } from '../helpers'
import { useBookingFormStore } from '../providers/BookingFormStoreProvider'
import { AccordionEnum, IVisitType } from '../store/data'
import { useAccordion } from './useAccordion'
import {
  HOTEL_PARAM,
  ROOM_TYPE_PARAM,
  useQueryParams,
  VISIT_END_PARAM,
  VISIT_START_PARAM,
  VISIT_TYPE_PARAM,
} from './useQueryParams'

export const useVisitTypeCategoriesAccordion = () => {
  const accordion = useBookingFormStore((state) => state.accordion)
  const language = useBookingFormStore((state) => state.language)
  const hotel = useBookingFormStore((state) => state.hotel)
  const destination = useBookingFormStore((state) => state.destination)
  const guest = useBookingFormStore((state) => state.guest)
  const roomType = useBookingFormStore((state) => state.roomType)
  const visitType = useBookingFormStore((state) => state.visitType)
  const changeVisitType = useBookingFormStore((state) => state.changeVisitType)
  const changeVisitTypeCategory = useBookingFormStore((state) => state.changeVisitTypeCategory)
  const changeHotel = useBookingFormStore((state) => state.changeHotel)
  const changeRoomType = useBookingFormStore((state) => state.changeRoomType)

  const { addQueryParameter, removeQueryParameter, removeQueryParametersArray } = useQueryParams()

  const { onChangeAccordion } = useAccordion()

  const removeRoomHotel = useCallback(async () => {
    changeHotel(null)
    changeRoomType(null)
    await removeQueryParametersArray([HOTEL_PARAM, ROOM_TYPE_PARAM])
  }, [changeHotel, changeRoomType, removeQueryParametersArray])

  const updateRouteQuery = useCallback(async (value: string | null | undefined) => {
    if (value) {
      await addQueryParameter(VISIT_TYPE_PARAM, value)
    } else {
      await removeQueryParameter(VISIT_TYPE_PARAM)
    }
  }, [addQueryParameter, removeQueryParameter])

  const clearVisitTypeSelection = useCallback(async () => {
    changeVisitType(null)
    changeVisitTypeCategory(null)
    changeRoomType(null)
    await removeQueryParametersArray([
      ROOM_TYPE_PARAM,
      VISIT_END_PARAM,
      VISIT_START_PARAM,
      VISIT_TYPE_PARAM,
    ])
  }, [
    changeRoomType,
    changeVisitType,
    changeVisitTypeCategory,
    removeQueryParametersArray,
  ])

  const validateVisitType = useCallback(async (value: IVisitType) => {
    if (hotel && !isVisitTypeSelectableForHotel(value, hotel.id, language)) {
      await clearVisitTypeSelection()
      return false
    }

    return true
  }, [clearVisitTypeSelection, hotel, language])

  const onChangeVisitType = useCallback(async (value: IVisitType | null) => {
    if (value) {
      const isValidVisitType = await validateVisitType(value)

      if (!isValidVisitType) {
        return
      }

      const nextAccordion = resolveNextAccordion(accordion as AccordionEnum, {
        destination: !!destination,
        guest: !!guest,
        hotel: !!hotel,
        roomType: !!roomType,
        visitType: !!visitType,
      })
      onChangeAccordion(nextAccordion)
    }

    changeVisitType(value)
    const visitTypeName = value?.name[getKeyLang(language)]
    await updateRouteQuery(visitTypeName)
  }, [
    accordion,
    changeVisitType,
    destination,
    guest,
    hotel,
    language,
    onChangeAccordion,
    roomType,
    updateRouteQuery,
    validateVisitType,
    visitType,
  ])

  const onInitVisitTypeValidation = useCallback(async (value: IVisitType | null) => {
    if (value) {
      await validateVisitType(value)
    } else {
      await removeQueryParameter(VISIT_TYPE_PARAM)
    }
  }, [removeQueryParameter, validateVisitType])

  return {
    clearVisitTypeSelection,
    onChangeVisitType,
    onInitVisitTypeValidation,
    removeRoomHotel,
  }
}
